/**
 * «Куда» на `/posts/:id`: аккаунты активной сети кнопками (aria-pressed) с городом и пометкой «уже в публикации N»;
 * кнопки (в пределах активной сети) «города платья» / «все» / «снять все»; у VK — «Метка места: город / без метки».
 *
 * У сети с единственным живым аккаунтом (обычно VK, Pinterest) выбирать нечего — вместо ряда «Отметить: …»
 * и списка аккаунтов рисуется одна кнопка-выключатель «Публикуем в ‹Сеть›» (`soleAccount`).
 *
 * Чистый компонент по пропсам: отдаёт правку (`Edits`), сохраняет страница. Архивный аккаунт виден, только
 * пока отмечен (снять можно, отметить заново — нет).
 */
import { Link } from "react-router";

import { btnGhost, input } from "@/components/trends/ui";
import { accountsEdit, checkedIds, cityIds, netIds, soleAccount, type Edits } from "@/lib/postDraft";
import { NETWORK_LABEL, type PostCity, type PostDetail } from "@/lib/postPlan";

export interface AccountPickerProps {
  plan: PostDetail;
  readOnly: boolean;
  /** Активная сеть (вкладка): рисуем и правим только её. */
  net: string;
  onChange: (edit: Edits) => void;
}

function VkLabel({ plan, readOnly, onChange }: Omit<AccountPickerProps, "net">) {
  const vk = plan.networks.vk;
  const salons: PostCity[] = plan.salons ?? [];
  const current = vk?.crm_location_id ?? null;
  const known = current === null || salons.some((s) => s.crm_location_id === current);
  return (
    <label className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted">
      Метка места
      <select
        aria-label="Метка места VK"
        className={`${input} w-56`}
        value={current === null ? "" : String(current)}
        disabled={readOnly}
        onChange={(e) => {
          const id = e.target.value === "" ? null : Number(e.target.value);
          const name = id === null ? null : (salons.find((s) => s.crm_location_id === id)?.name ?? vk?.city_name ?? null);
          onChange({ networks: { vk: { crm_location_id: id, city_name: name } } });
        }}
      >
        <option value="">без метки</option>
        {!known ? <option value={String(current)}>{vk?.city_name ?? `салон ${current}`}</option> : null}
        {salons.map((s) => (
          <option key={s.crm_location_id} value={String(s.crm_location_id)}>
            {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AccountPicker({ plan, readOnly, net, onChange }: AccountPickerProps) {
  const checked = new Set(checkedIds(plan));
  const inNet = new Set(netIds(plan, net));
  const outside = [...checked].filter((id) => !inNet.has(id)); // отметки других сетей не трогаем
  const block = plan.accounts.find((n) => n.code === net);
  const shown = (block?.accounts ?? []).filter((a) => !a.archived || a.checked);
  const cities = cityIds(plan).filter((id) => inNet.has(id));
  const set = (ids: number[]) => onChange(accountsEdit(plan, [...outside, ...ids]));
  const toggle = (id: number) => set(checked.has(id) ? [...checked].filter((x) => inNet.has(x) && x !== id) : [...[...checked].filter((x) => inNet.has(x)), id]);
  const cityNames = (plan.dress.cities ?? []).map((c) => c.name).join(", ");
  const liveAll = shown.map((a) => a.id);
  const checkedHere = shown.filter((a) => a.checked).length;
  // Выключатель — только когда показывать больше нечего: иначе отмеченный архивный аккаунт
  // при живом единственном схлопнул бы список в недоступную для снятия кнопку (тупик).
  const sole = shown.length === 1 ? soleAccount(plan, net) : null;
  const label = NETWORK_LABEL[net as keyof typeof NETWORK_LABEL] ?? net;
  const vkLabel = net === "vk" && plan.networks.vk?.active ? <VkLabel plan={plan} readOnly={readOnly} onChange={onChange} /> : null;

  if (sole) {
    return (
      <div className="grid gap-3" data-annot="куда">
        <div className="grid gap-1.5" data-annot={`аккаунты ${net}`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              aria-pressed={sole.checked}
              disabled={readOnly}
              title={sole.name ?? undefined}
              onClick={() => toggle(sole.id)}
              className={
                "flex items-center gap-1.5 rounded-[var(--radius-ui)] border px-2.5 py-1.5 text-[12.5px] " +
                (sole.checked ? "border-accent bg-accent text-bg" : "border-border bg-bg text-fg hover:bg-bg-subtle")
              }
            >
              <span>Публикуем в {label}</span>
              <span className={sole.checked ? "text-[11px] opacity-80" : "text-[11px] text-fg-muted"}>
                {sole.handle} · {sole.city_name ?? "федеральный"}
              </span>
            </button>
            {sole.also_in.length > 0 ? (
              <span className="text-[11px] text-fg-faint">
                уже в публикации{" "}
                {sole.also_in.map((id, i) => (
                  <span key={id}>
                    {i > 0 ? ", " : ""}
                    <Link to={`/posts/${id}`} className="text-accent-text hover:underline">
                      №{id}
                    </Link>
                  </span>
                ))}
              </span>
            ) : null}
          </div>
          {vkLabel}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-3" data-annot="куда">
      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[12px] text-fg-faint">Отметить:</span>
        <button
          type="button"
          className={btnGhost}
          disabled={readOnly || cities.length === 0}
          title={cityNames ? `Города наличия: ${cityNames}` : "У платья нет городов наличия (или CRM недоступна)"}
          onClick={() => set(cities)}
        >
          города платья
        </button>
        <button type="button" className={btnGhost} disabled={readOnly} onClick={() => set(liveAll)}>
          все
        </button>
        <button type="button" className={btnGhost} disabled={readOnly || checkedHere === 0} onClick={() => set([])}>
          снять все
        </button>
      </div>
      <div className="grid gap-1.5" data-annot={`аккаунты ${net}`}>
        {shown.length === 0 ? (
          <span className="text-[12px] text-fg-faint">
            Аккаунтов нет —{" "}
            <Link to="/posts?tab=accounts" className="text-accent-text hover:underline">
              добавить
            </Link>
          </span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {shown.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  aria-pressed={a.checked}
                  disabled={readOnly || (a.archived && !a.checked)}
                  title={a.name ?? undefined}
                  onClick={() => toggle(a.id)}
                  className={
                    "flex items-center gap-1.5 rounded-[var(--radius-ui)] border px-2.5 py-1.5 text-[12.5px] disabled:opacity-50 " +
                    (a.checked ? "border-accent bg-accent text-bg" : "border-border bg-bg text-fg hover:bg-bg-subtle")
                  }
                >
                  <span>{a.handle}</span>
                  <span className={a.checked ? "text-[11px] opacity-80" : "text-[11px] text-fg-muted"}>
                    {a.city_name ?? "федеральный"}
                  </span>
                </button>
                {a.archived ? <span className="text-[11px] text-warn">в архиве</span> : null}
                {a.also_in.length > 0 ? (
                  <span className="text-[11px] text-fg-faint">
                    уже в публикации{" "}
                    {a.also_in.map((id, i) => (
                      <span key={id}>
                        {i > 0 ? ", " : ""}
                        <Link to={`/posts/${id}`} className="text-accent-text hover:underline">
                          №{id}
                        </Link>
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {vkLabel}
      </div>
    </div>
  );
}
